import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { getHeaderTheme } from "../config/headerTheme";

/**
 * VoiceInput - Input completo del chat de ALMA (texto + voz).
 *
 * Estados visuales:
 *   1. Campo vacío   → textarea + 🎤 micrófono
 *   2. Escribiendo   → textarea + ➤ botón enviar
 *   3. Grabando      → barra de grabación con ondas, contador, 🗑️ cancelar, ➤ enviar
 *
 * En iOS/iPadOS se oculta el micrófono (Web Speech API falla en PWA standalone).
 */

const SILENCE_TIMEOUT_MS = 1500;

// Detectar iOS / iPadOS
const isIOSDevice =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// Chrome en iOS tiene "CriOS" en el user agent
const isChromeOnIOS = isIOSDevice && /CriOS/.test(navigator.userAgent || "");

// Ocultar micrófono en iOS excepto Chrome (para testear)
const hideMicOnIOS = isIOSDevice && !isChromeOnIOS;

// ─── Iconos SVG ─────────────────────────────────────────────────────────────

const MicIcon = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 15.5C14.21 15.5 16 13.71 16 11.5V6C16 3.79 14.21 2 12 2C9.79 2 8 3.79 8 6V11.5C8 13.71 9.79 15.5 12 15.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.3501 9.6499V11.3499C4.3501 15.5699 7.7801 18.9999 12.0001 18.9999C16.2201 18.9999 19.6501 15.5699 19.6501 11.3499V9.6499" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.6101 6.43012C11.5101 6.10012 12.4901 6.10012 13.3901 6.43012" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.2 8.55007C11.73 8.41007 12.28 8.41007 12.81 8.55007" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 19V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SendIcon = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.51002 4.23001L18.07 8.51001C21.91 10.43 21.91 13.57 18.07 15.49L9.51002 19.77C3.75002 22.65 1.40002 20.29 4.28002 14.54L5.15002 12.81C5.37002 12.37 5.37002 11.64 5.15002 11.2L4.28002 9.46001C1.40002 3.71001 3.76002 1.35001 9.51002 4.23001Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.44 12H10.84" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.33 16.5H13.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.5 12.5H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Componente ─────────────────────────────────────────────────────────────

const VoiceInput = ({ value, onChange, onSend, disabled, placeholder, rows = 1 }) => {
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [startError, setStartError] = useState(null);
  const hasSentRef = useRef(false);
  const silenceTimeoutRef = useRef(null);

  // El transcript ya no se envía automáticamente al dejar de hablar.
  // El usuario debe tocar el botón de enviar para mandar el texto.

  // Contador de duración de grabación
  useEffect(() => {
    if (!isRecording) {
      setRecordingDuration(0);
      return;
    }
    const interval = setInterval(() => {
      setRecordingDuration((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isRecording]);

  // No hay auto-detener por silencio — el usuario controla cuándo enviar o cancelar.

  // Si se deshabilita mientras graba, cancelar
  useEffect(() => {
    if (disabled && isRecording) {
      hasSentRef.current = true;
      SpeechRecognition.stopListening();
      setIsRecording(false);
    }
  }, [disabled, isRecording]);

  const startRecording = useCallback(async () => {
    setStartError(null);
    hasSentRef.current = false;
    resetTranscript();
    setIsRecording(true);
    try {
      await SpeechRecognition.startListening({
        continuous: true,
        language: "es-ES",
      });
    } catch (err) {
      console.error("[VoiceInput] startListening error:", err);
      setStartError("No se pudo iniciar");
      setIsRecording(false);
    }
  }, [resetTranscript]);

  const sendRecording = useCallback(() => {
    SpeechRecognition.stopListening();
    const text = transcript.trim();
    if (text) {
      onSend(text);
    }
    resetTranscript();
    setIsRecording(false);
    hasSentRef.current = false;
  }, [transcript, onSend, resetTranscript]);

  const cancelRecording = useCallback(() => {
    SpeechRecognition.stopListening();
    resetTranscript();
    setIsRecording(false);
    hasSentRef.current = false;
  }, [resetTranscript]);

  const handleSendText = useCallback(() => {
    const text = value.trim();
    if (!text) return;
    onSend(text);
    onChange("");
  }, [value, onSend, onChange]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendText();
      }
    },
    [handleSendText]
  );

  const showMic = browserSupportsSpeechRecognition && !hideMicOnIOS;

  return (
    <InputContainer>
      {isRecording ? (
        <RecordingBar>
          <AudioWaves>
            {[...Array(5)].map((_, i) => (
              <WaveBar key={i} $index={i} />
            ))}
          </AudioWaves>
          <Duration>{formatDuration(recordingDuration)}</Duration>
          <TranscriptDisplay>{transcript || "Escuchando..."}</TranscriptDisplay>
          <RecordingActions>
            <IconButton onClick={cancelRecording} $variant="cancel" title="Cancelar">
              <TrashIcon />
            </IconButton>
            <IconButton onClick={sendRecording} $variant="send" title="Enviar">
              <SendIcon />
            </IconButton>
          </RecordingActions>
        </RecordingBar>
      ) : (
        <>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={2000}
            rows={rows}
            disabled={disabled}
          />
          {value.trim() ? (
            <IconButton onClick={handleSendText} disabled={disabled} $variant="send" title="Enviar">
              <SendIcon />
            </IconButton>
          ) : showMic ? (
            <IconButton onClick={startRecording} disabled={disabled} $variant="mic" title="Hablar para escribir">
              <MicIcon />
            </IconButton>
          ) : (
            <IconButton onClick={handleSendText} disabled={disabled || !value.trim()} $variant="send" title="Enviar">
              <SendIcon />
            </IconButton>
          )}
        </>
      )}
      {startError && <DebugText>{startError}</DebugText>}
    </InputContainer>
  );
};

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default VoiceInput;

// ─── Estilos ─────────────────────────────────────────────────────────────────

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  position: relative;
`;

const Textarea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 0.6rem 0.8rem;
  font-size: 0.95rem;
  font-family: inherit;
  line-height: 1.4;
  outline: none;
  min-height: 44px;
  &:focus {
    border-color: #999;
  }
  &:disabled {
    background: #f5f5f5;
    opacity: 0.6;
  }
`;

const theme = getHeaderTheme();

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: filter 0.2s ease, opacity 0.15s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  background: ${({ $variant }) => {
    if ($variant === "send") return theme.primary;
    if ($variant === "cancel") return theme.primaryLight;
    return "#f3f4f6";
  }};
  color: ${({ $variant }) => {
    if ($variant === "send") return theme.text;
    if ($variant === "cancel") return theme.accent;
    return "#374151";
  }};

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const RecordingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  padding: 0.6rem 0.8rem;
  border: 1px solid ${theme.accent};
  border-radius: 12px;
  background: ${theme.primaryLight};
  min-height: 44px;
`;

const AudioWaves = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 24px;
`;

const waveAnim = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

const WaveBar = styled.div`
  width: 3px;
  height: 100%;
  background: ${theme.accent};
  border-radius: 2px;
  animation: ${waveAnim} 1s ease-in-out infinite;
  animation-delay: ${({ $index }) => $index * 0.15}s;
`;

const Duration = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.accent};
  font-variant-numeric: tabular-nums;
  min-width: 40px;
`;

const TranscriptDisplay = styled.span`
  flex: 1;
  font-size: 0.9rem;
  color: #111;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
  padding: 0 0.3rem;
  line-height: 1.4;
  max-height: 120px;
  overflow-y: auto;
`;

const RecordingActions = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-left: auto;
`;

const DebugText = styled.span`
  position: absolute;
  bottom: -18px;
  left: 0;
  font-size: 10px;
  color: #dc2626;
  white-space: nowrap;
`;
